// Math & Bit Manipulation — Basic vs Optimized code for every "Beginner Problems" drill listed
// in the Math & Bit Manipulation Explanation modal. Same shape as intervalDrills.js: concise
// steps + an interactive "watch it happen" visualizer per version. Three drills cross-reference
// curated problems instead of duplicating content.

const mathBitDrills = [
  {
    id: 'single-number-drill',
    title: 'Single Number',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Single Number\" there for the complete step-by-step and both code versions. You can also try it hands-on " +
      "in the \"Single Number (XOR)\" mode of the Interactive Practice playground.",
  },
  {
    id: 'reverse-bits',
    title: 'Reverse Bits',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Convert to a Binary String and Reverse It',
        explanation: 'Format the number as a 32-character binary string, reverse the string with a StringBuilder, and parse it back into a number. Correct, but allocates several strings per call and pays string-manipulation overhead for something that\'s fundamentally a bit-level operation.',
        steps: [
          'Format the input as a zero-padded 32-character binary string.',
          'Reverse that string.',
          'Parse the reversed string back into a numeric value.',
          'Every call allocates multiple intermediate String and StringBuilder objects.',
        ],
        code:
`int reverseBitsBasic(int n) {
    String binary = String.format("%32s", Integer.toBinaryString(n)).replace(' ', '0');
    String reversed = new StringBuilder(binary).reverse().toString();
    return (int) Long.parseLong(reversed, 2);
}`,
        complexity: { time: 'O(32) with string allocation overhead', space: 'O(32) — several intermediate strings' },
      },
      {
        name: 'Optimized — Bitwise Shift, No Allocation',
        explanation: 'Peel off the input\'s lowest bit one at a time, shifting it into the result from the other end. No strings, no parsing — pure bit manipulation with a fixed 32 iterations.',
        steps: [
          'For each of 32 bit positions: shift the result left by one (making room for a new bit).',
          'OR in the input\'s current lowest bit (n & 1).',
          'Shift the input right by one, discarding the bit just consumed.',
          'After 32 iterations, the result holds every bit of the input in reversed order.',
        ],
        visual: {
          arrays: [{ label: 'input bits (8-bit illustration of 00000001)', values: ['0', '0', '0', '0', '0', '0', '0', '1'] }],
          varNames: ['result'],
          steps: [
            { vars: { result: '00000001' }, note: 'i=0: result = (0 << 1) | (1 & 1) = 1. n becomes 0 (n >>= 1).' },
            { vars: { result: '00000010' }, note: 'i=1: result = (1 << 1) | (0 & 1) = 2. n stays 0.' },
            { vars: { result: '00000100' }, note: 'i=2: result = (2 << 1) | 0 = 4.' },
            { vars: { result: '10000000' }, note: 'Continuing this way for the remaining bits (all 0, since n became 0 early), result reaches 128 after 8 total shifts — the original single 1-bit, which started at the LEAST significant position, has moved all the way to the MOST significant position. This is the same algorithm the real 32-bit version runs, just shown here over 8 bits for clarity.' },
          ],
        },
        code:
`public int reverseBits(int n) {
    int result = 0;
    for (int i = 0; i < 32; i++) {
        result = (result << 1) | (n & 1);
        n >>= 1;
    }
    return result;
}`,
        complexity: { time: 'O(32) = O(1)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'power-of-two',
    title: 'Power of Two',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Repeated Division',
        explanation: 'Repeatedly divide by 2 as long as the number is even, and check whether it eventually reaches exactly 1. Correct, but takes up to log₂(n) division steps instead of a single bit operation.',
        steps: [
          'If n is 0 or negative, it can\'t be a power of two — return false immediately.',
          'While n is even, divide it by 2.',
          'Once n is odd, check whether it equals exactly 1.',
          'If it does, every factor removed was a 2, and nothing else — a power of two.',
        ],
        code:
`boolean isPowerOfTwoBasic(int n) {
    if (n <= 0) return false;
    while (n % 2 == 0) {
        n /= 2;
    }
    return n == 1;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Bit Trick: n & (n-1)',
        explanation: 'A power of two has EXACTLY one set bit in binary (1, 10, 100, 1000, ...). Subtracting 1 from it flips that single bit to 0 and every bit below it to 1 — so ANDing the original with (n-1) always produces 0 for a true power of two, and something nonzero otherwise.',
        steps: [
          'First confirm n is positive — 0 and negative numbers are never powers of two.',
          'Compute n - 1.',
          'AND n with (n - 1).',
          'If the result is exactly 0, n had only one set bit — it\'s a power of two.',
        ],
        visual: {
          arrays: [{ label: 'n = 16 (binary)', values: ['1', '0', '0', '0', '0'] }],
          varNames: [],
          steps: [
            { note: 'n = 16 = 10000. n - 1 = 15 = 01111.' },
            { note: 'n & (n-1) = 10000 & 01111 = 00000 = 0. Since n > 0 and the result is 0 — 16 IS a power of two.' },
            { note: 'Contrast with n = 3 = 011: n-1 = 2 = 010. n & (n-1) = 011 & 010 = 010 = 2 ≠ 0 — NOT a power of two.' },
          ],
        },
        code:
`boolean isPowerOfTwo(int n) {
    return n > 0 && (n & (n - 1)) == 0;
}`,
        complexity: { time: 'O(1)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'number-of-1-bits-drill',
    title: 'Number of 1 Bits',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough — including Brian Kernighan's bit-clearing trick — lives " +
      "in this pattern's curated problem list — open \"Number of 1 Bits\" there for the complete step-by-step.",
  },
  {
    id: 'sum-of-two-integers',
    title: 'Sum of Two Integers (No + or -)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Simulate With Increment/Decrement Loops',
        explanation: 'Since the built-in + and - operators are off-limits, simulate addition by repeatedly applying ++ or -- one unit at a time. Technically avoids + and -, but is painfully slow for large magnitudes, and doesn\'t demonstrate the real bit-level insight the problem is testing.',
        steps: [
          'If b is 0, there\'s nothing left to add — return a as-is.',
          'If b is positive, increment a and decrement b, one at a time, until b reaches 0.',
          'If b is negative, decrement a and increment b, one at a time, until b reaches 0.',
          'Return the final value of a.',
        ],
        code:
`int getSumBasic(int a, int b) {
    if (b == 0) return a;
    if (b > 0) {
        while (b > 0) { a++; b--; }
    } else {
        while (b < 0) { a--; b++; }
    }
    return a;
}`,
        complexity: { time: 'O(|b|) — linear in the magnitude of b', space: 'O(1)' },
      },
      {
        name: 'Optimized — XOR for the Sum, AND-Shift for the Carry',
        explanation: 'Binary addition without a carry is exactly XOR — bits that differ become 1, matching bits become 0. The CARRY that XOR misses is exactly the bits where BOTH inputs were 1, shifted one position left. Repeating this — treating the carry as a new "b" to add in — converges once there\'s no carry left.',
        steps: [
          'Compute sumWithoutCarry = a XOR b.',
          'Compute carry = (a AND b) shifted left by 1.',
          'Set a = sumWithoutCarry and b = carry, and repeat.',
          'The loop ends the moment carry (now b) becomes 0 — a then holds the true sum.',
        ],
        visual: {
          arrays: [],
          varNames: ['a', 'b'],
          steps: [
            { vars: { a: '010 (2)', b: '011 (3)' }, note: 'Start: a=2 (010), b=3 (011).' },
            { vars: { a: '001', b: '100' }, note: 'sumWithoutCarry = 010^011 = 001. carry = (010&011)<<1 = 010<<1 = 100. New a=001, b=100.' },
            { vars: { a: '101', b: '000' }, note: 'sumWithoutCarry = 001^100 = 101. carry = (001&100)<<1 = 000<<1 = 000. New a=101, b=000.' },
            { vars: { a: '101 (5)', b: '0' }, note: 'b is now 0 — loop ends. Final answer: a = 101 = 5, matching 2 + 3.' },
          ],
        },
        code:
`int getSum(int a, int b) {
    while (b != 0) {
        int carry = (a & b) << 1;
        a = a ^ b;
        b = carry;
    }
    return a;
}`,
        complexity: { time: 'O(32) = O(1) — bounded by the integer bit width', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'counting-bits-drill',
    title: 'Counting Bits',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Counting Bits\" there for the complete step-by-step. You can also try it hands-on in the \"Counting Bits\" " +
      "mode of the Interactive Practice playground.",
  },
  {
    id: 'reverse-integer',
    title: 'Reverse Integer',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Convert to String, Reverse, Parse Back',
        explanation: 'Convert the number to a string (handling the sign separately), reverse the string, and parse it back — using a wider type (long) to check for 32-bit overflow AFTER the fact. Correct, but relies on string allocation and a try/catch safety net.',
        steps: [
          'Record the sign, and work with the absolute value as a string.',
          'Reverse the string with a StringBuilder.',
          'Parse the reversed string as a long (wider than int, to safely detect overflow).',
          'If the result falls outside the 32-bit signed integer range, return 0; otherwise return it (with the sign restored).',
        ],
        code:
`int reverseBasic(int x) {
    boolean negative = x < 0;
    String s = String.valueOf(Math.abs((long) x));
    String reversed = new StringBuilder(s).reverse().toString();
    long result = Long.parseLong(reversed);
    if (negative) result = -result;
    if (result < Integer.MIN_VALUE || result > Integer.MAX_VALUE) return 0;
    return (int) result;
}`,
        complexity: { time: 'O(digits)', space: 'O(digits) — string allocation' },
      },
      {
        name: 'Optimized — Digit-by-Digit Arithmetic, Overflow Checked BEFORE It Happens',
        explanation: 'Peel off digits with % and / directly on the integer — no strings at all. Critically, check whether the NEXT multiplication would overflow BEFORE performing it, by comparing against Integer.MAX_VALUE / 10 — this avoids ever triggering undefined overflow behavior in the first place.',
        steps: [
          'While x has digits left, extract the last digit with x % 10, then remove it with x /= 10.',
          'Before appending the digit to the result, check: would result * 10 + digit exceed the 32-bit range? If so, return 0 immediately.',
          'Otherwise, append: result = result * 10 + digit.',
          'Return the final result once x reaches 0.',
        ],
        visual: {
          arrays: [{ label: 'x = 123', values: ['1', '2', '3'] }],
          varNames: ['result'],
          steps: [
            { vars: { result: 0 }, note: 'digit = 123 % 10 = 3. x = 123 / 10 = 12. No overflow risk. result = 0×10 + 3 = 3.' },
            { vars: { result: 3 }, note: 'digit = 12 % 10 = 2. x = 12 / 10 = 1. result = 3×10 + 2 = 32.' },
            { vars: { result: 32 }, note: 'digit = 1 % 10 = 1. x = 1 / 10 = 0. result = 32×10 + 1 = 321.' },
            { vars: { result: 321 }, note: 'x is now 0 — loop ends. Final answer: 321.' },
          ],
        },
        code:
`int reverse(int x) {
    int result = 0;
    while (x != 0) {
        int digit = x % 10;
        x /= 10;
        if (result > Integer.MAX_VALUE / 10 || (result == Integer.MAX_VALUE / 10 && digit > 7)) return 0;
        if (result < Integer.MIN_VALUE / 10 || (result == Integer.MIN_VALUE / 10 && digit < -8)) return 0;
        result = result * 10 + digit;
    }
    return result;
}`,
        complexity: { time: 'O(digits)', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'gcd-of-two-numbers',
    title: 'Greatest Common Divisor of Two Numbers',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Repeated Subtraction',
        explanation: 'Repeatedly subtract the smaller number from the larger one until they\'re equal — that common value is the GCD. Correct (this is literally Euclid\'s ORIGINAL algorithm), but can take enormously many steps when the two numbers differ greatly in magnitude.',
        steps: [
          'While the two numbers are different, subtract the smaller from the larger.',
          'Repeat until both numbers are equal.',
          'That shared value is the greatest common divisor.',
          'For very different magnitudes (like gcd(1, 1000000)), this can take up to a million steps.',
        ],
        code:
`int gcdBasic(int a, int b) {
    while (a != b) {
        if (a > b) a -= b;
        else b -= a;
    }
    return a;
}`,
        complexity: { time: 'O(max(a,b) / min(a,b)) worst case', space: 'O(1)' },
      },
      {
        name: 'Optimized — Euclidean Algorithm With Modulo',
        explanation: 'Instead of subtracting one copy of the smaller number at a time, the modulo operator effectively subtracts it as many times as possible in a single step. This converges dramatically faster — the number of steps is bounded by the Fibonacci sequence\'s growth rate, i.e. O(log) instead of O(n).',
        steps: [
          'While b is not 0, compute a % b.',
          'Shift: the new b becomes that remainder, and the new a becomes the old b.',
          'Repeat until b reaches exactly 0.',
          'The value left in a is the greatest common divisor.',
        ],
        visual: {
          arrays: [],
          varNames: ['a', 'b'],
          steps: [
            { vars: { a: 48, b: 18 }, note: 'Start: a=48, b=18.' },
            { vars: { a: 18, b: 12 }, note: '48 % 18 = 12. New a=18, b=12.' },
            { vars: { a: 12, b: 6 }, note: '18 % 12 = 6. New a=12, b=6.' },
            { vars: { a: 6, b: 0 }, note: '12 % 6 = 0. New a=6, b=0. b is now 0 — loop ends. GCD = 6.' },
          ],
        },
        code:
`int gcd(int a, int b) {
    while (b != 0) {
        int temp = b;
        b = a % b;
        a = temp;
    }
    return a;
}`,
        complexity: { time: 'O(log(min(a, b)))', space: 'O(1)' },
      },
    ],
  },
  {
    id: 'pow-x-n',
    title: 'Pow(x, n)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Multiply x by Itself n Times',
        explanation: 'The direct translation of what exponentiation means: multiply the running result by x, once for every unit of n. Correct, but takes n full multiplications even though far fewer are actually necessary.',
        steps: [
          'If n is negative, invert x and negate n (x^-n = (1/x)^n).',
          'Start result at 1.',
          'Multiply result by x, n times in a row.',
          'Return the final result.',
        ],
        code:
`double myPowBasic(double x, int n) {
    if (n < 0) { x = 1 / x; n = -n; }
    double result = 1.0;
    for (int i = 0; i < n; i++) {
        result *= x;
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Binary Exponentiation (Fast Power)',
        explanation: 'Instead of multiplying by x one unit of n at a time, repeatedly SQUARE x while halving n. Each halving of n doubles what one multiplication of x represents — so only the BITS of n that are actually 1 need to contribute their corresponding squared power to the result.',
        steps: [
          'If n is negative, invert x and work with -n instead.',
          'While n is greater than 0: if the current bit of n is 1, multiply it into the result.',
          'Square x (it now represents twice as many multiplications per use), and halve n (shift right by 1).',
          'Repeat until n reaches 0 — the accumulated result is x^n.',
        ],
        visual: {
          arrays: [],
          varNames: ['x', 'N', 'result'],
          steps: [
            { vars: { x: 2, N: 10, result: 1 }, note: 'Start: x=2, N=10 (1010 in binary), result=1.' },
            { vars: { x: 4, N: 5, result: 1 }, note: 'N=10 is even (bit=0) — result unchanged. x=2×2=4. N=10>>1=5.' },
            { vars: { x: 16, N: 2, result: 4 }, note: 'N=5 is odd (bit=1) — result=1×4=4. x=4×4=16. N=5>>1=2.' },
            { vars: { x: 256, N: 1, result: 4 }, note: 'N=2 is even — result unchanged. x=16×16=256. N=2>>1=1.' },
            { vars: { x: 65536, N: 0, result: 1024 }, note: 'N=1 is odd — result=4×256=1024. x=256×256=65536. N=1>>1=0. Loop ends. Final answer: 1024 = 2^10.' },
          ],
        },
        code:
`double myPow(double x, int n) {
    long N = n;
    if (N < 0) { x = 1 / x; N = -N; }
    double result = 1.0;
    while (N > 0) {
        if ((N & 1) == 1) result *= x;
        x *= x;
        N >>= 1;
    }
    return result;
}`,
        complexity: { time: 'O(log n)', space: 'O(1)' },
      },
    ],
  },
];

export default mathBitDrills;
